import { config } from '@/config';
import { tokenStore } from './token-store';

export class ApiError extends Error {
  constructor(
    public status: number,
    public code: string,
    message: string,
    public detail?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

interface RequestOptions {
  method?: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE';
  body?: unknown;
  query?: Record<string, string | number | boolean | undefined | null>;
  /** Skip Authorization header — used by login/register/refresh themselves. */
  unauthenticated?: boolean;
  signal?: AbortSignal;
}

interface ProblemJson {
  type?: string;
  title?: string;
  status?: number;
  detail?: string;
  code?: string;
}

function buildUrl(path: string, query?: RequestOptions['query']): string {
  // Concatenate, never URL-join: a tenant-host base carries /api and an
  // absolute path passed to `new URL(path, base)` would drop it.
  const url = new URL(config.apiUrl + path);
  if (query) {
    for (const [k, v] of Object.entries(query)) {
      if (v === undefined || v === null) continue;
      url.searchParams.set(k, String(v));
    }
  }
  return url.toString();
}

/**
 * Get a valid access token. Refreshes if expired/missing and a refresh
 * token is available. Returns null if the user is fully signed out.
 */
let inflightRefresh: Promise<string | null> | null = null;
async function getValidAccessToken(): Promise<string | null> {
  if (tokenStore.hasValidAccess()) return tokenStore.getAccess();

  // Coalesce concurrent refresh attempts so we don't make N round trips.
  if (inflightRefresh) return inflightRefresh;

  const refresh = tokenStore.getRefresh();
  if (!refresh) return null;

  inflightRefresh = (async () => {
    try {
      const res = await fetch(buildUrl('/v1/auth/refresh'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ refreshToken: refresh }),
      });
      if (!res.ok) {
        // Refresh token rejected — mark as expired so the login page can
        // explain why the user landed back on it.
        tokenStore.markExpired();
        return null;
      }
      const data = (await res.json()) as {
        tokens: {
          accessToken: string;
          refreshToken: string;
          accessTokenExpiresAt: string;
        };
      };
      tokenStore.setAccess(data.tokens.accessToken, data.tokens.accessTokenExpiresAt);
      tokenStore.setRefresh(data.tokens.refreshToken);
      return data.tokens.accessToken;
    } finally {
      inflightRefresh = null;
    }
  })();

  return inflightRefresh;
}

export async function api<T = unknown>(
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = {
    Accept: 'application/json',
  };

  if (!opts.unauthenticated) {
    const token = await getValidAccessToken();
    if (token) headers['Authorization'] = `Bearer ${token}`;
  }

  const init: RequestInit = {
    method: opts.method ?? 'GET',
    headers,
  };
  if (opts.signal) init.signal = opts.signal;
  if (opts.body !== undefined) {
    headers['Content-Type'] = 'application/json';
    init.body = JSON.stringify(opts.body);
  }

  const res = await fetch(buildUrl(path, opts.query), init);

  if (res.status === 204 || res.headers.get('content-length') === '0') {
    return undefined as T;
  }

  const text = await res.text();
  let parsed: unknown;
  try {
    parsed = text ? JSON.parse(text) : undefined;
  } catch {
    throw new ApiError(res.status, 'invalid_response', 'Invalid JSON response');
  }

  if (!res.ok) {
    const problem = (parsed ?? {}) as ProblemJson;
    throw new ApiError(
      res.status,
      problem.code ?? 'http_error',
      problem.detail ?? problem.title ?? `Request failed with ${res.status}`,
      problem,
    );
  }

  return parsed as T;
}
